/* 
 * Copyright (C) 2022 SHC Holdings LLC.  All Rights Reserved. 
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { FormGroup, Label } from 'reactstrap';
import { Button, FormFeedback, MessageBox, useAuth, useForm, useToggle, Input, useMount, useIndexName } from 'nmqf-common-ui';
import { ChangePasswordRequiredModal } from './changepasswordrequired';

const Form = styled.form`
  max-width: 25rem;
  width: 100%;
  margin: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 2rem;
`;

const validationSchema = Yup.object().shape({
  username: Yup.string().trim().required('Required'),
  password: Yup.string().required('Required'),
});

export const LoginComponent: React.FC = (props) => {
  const auth = useAuth();
  const error = auth.getError();
  const authChallenge = auth.getAuthChallenge();
  const [errorMessage, setErrorMessage] = useState<any>();
  const [showChangePassModal, toggleChangePassModal] = useToggle();
  const indexName = useIndexName();

  const { setInitialValues, values, handleChange, handleBlur, handleSubmit, isSubmitting, getError } = useForm({ validationSchema });

  useMount(() => {
    setInitialValues({
      username: '',
      password: '',
    });
    return () => {
      auth.resetErrorAndChallenge();
    };
  });

  useEffect(() => {
    setErrorMessage(undefined);
    if (authChallenge?.ChallengeName) {
      toggleChangePassModal();
    } else if (error) {
      setErrorMessage(error);
    }
  }, [authChallenge, error, toggleChangePassModal]);

  const onSubmit = handleSubmit(async (values) => {
    await auth.authenticate({ ...values, indexName });
  });

  return (
    <>
      <Form onSubmit={onSubmit}>
        <MessageBox error={errorMessage} />
        <FormGroup>
          <Label>Username</Label>
          <Input
            type="text"
            name="username"
            placeholder="Username"
            value={values.username}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={!!getError('username')}
          />
          <FormFeedback message={getError('username')} />
        </FormGroup>
        <FormGroup>
          <Label>Password</Label>
          <Input
            type="password"
            name="password"
            placeholder="Password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={!!getError('password')}
          />
          <FormFeedback message={getError('password')} />
        </FormGroup>
        <div className="text-right">
          <Button color="primary" type="submit" loading={isSubmitting} message="Log in" />
        </div>
      </Form>
      <ChangePasswordRequiredModal username={values.username} sessionId={authChallenge?.Session} isOpen={showChangePassModal} toggle={toggleChangePassModal} />
    </>
  );
};
